import { Box, Chip, Paper, Tooltip, Typography } from "@mui/material";
import HtmlWithLatex from "./HtmlWithLatex";

/** Inline-safe LaTeX answer: renders in a span-like box so it flows inside Typography. */
const LatexValue = ({ value, sx }) => (
  <Box component="span" sx={{ display: "inline-block", verticalAlign: "middle", ...sx }}>
    <HtmlWithLatex html={value == null ? "" : String(value)} />
  </Box>
);

/**
 * Renders the read-only answer/options section for a question in view mode.
 * Used for both standalone questions and sub-questions inside grouped questions.
 * Handles both admin format (answer_data.*) and examplify legacy format (blanks, answer).
 */
// Wrap bare TeX commands like \square in $...$ so MathJax renders them.
const toLatexLabel = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (s.includes("$")) return s;
  if (/^\\[a-zA-Z]+(\s+\\[a-zA-Z]+)*$/.test(s)) {
    return `$${s}$`;
  }
  return s;
};

/** Similar-paper AI may return correct_answer_text as an array — flatten for display */
const formatAnswerForDisplay = (val) => {
  if (val == null || val === "") return "";
  if (Array.isArray(val)) {
    return val.map((v) => formatAnswerForDisplay(v)).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

/** Small chip showing where the answer came from. */
export const AnswerSourceBadge = ({ answerData, sx = {} }) => {
  const source = answerData?.answer_source;
  if (!source) return null;

  if (source === "answer_sheet") {
    return (
      <Tooltip title="Answer extracted from the PDF's answer key">
        <Chip
          label="From PDF answer key"
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 20, ...sx }}
        />
      </Tooltip>
    );
  }

  if (source === "ai_generated" || source === "ai") {
    return (
      <Tooltip title="Answer was generated by AI — verify before confirming">
        <Chip
          label="AI generated"
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 20, ...sx }}
        />
      </Tooltip>
    );
  }

  if (source === "programmatic") {
    return (
      <Tooltip title="Answer solved automatically from math computation">
        <Chip
          label="Auto-solved"
          size="small"
          color="secondary"
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 20, ...sx }}
        />
      </Tooltip>
    );
  }

  if (source === "cross_validated") {
    return (
      <Tooltip title="Answer confirmed by both AI and math solver">
        <Chip
          label="Cross-validated"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 20, ...sx }}
        />
      </Tooltip>
    );
  }

  return null;
};

const PDF_ANSWER_PLACEHOLDERS = [
  "[see answer sheet a]",
  "[see answer sheet b]",
  "[solution requires manual entry]",
];

const isPdfPlaceholder = (text) =>
  PDF_ANSWER_PLACEHOLDERS.includes(String(text ?? "").trim().toLowerCase());

const QuestionAnswerDisplay = ({ question }) => {
  if (!question) return null;

  const { type, options = [] } = question;

  // When the PDF answer is a placeholder and an AI-computed answer exists, show the AI answer.
  const rawAnswerData = question.answer_data ?? {};
  const hasAiAnswer = !!question.ai_answer_data;
  const pdfIsPlaceholder = isPdfPlaceholder(rawAnswerData?.correct_answer_text);
  const answer_data = (pdfIsPlaceholder && hasAiAnswer) ? question.ai_answer_data : rawAnswerData;
  const usingAiFallback = pdfIsPlaceholder && hasAiAnswer;

  const rawCat =
    answer_data?.correct_answer_text ?? question.answer ?? question.answer_text ?? "";
  const rawModel = answer_data?.model_answer ?? "";
  let correctAnswerText = rawCat;
  if (type === "essay") {
    correctAnswerText = rawModel || rawCat;
  } else if (type === "short-answer" && !correctAnswerText) {
    correctAnswerText = rawModel;
  }

  const content = renderAnswerContent({ type, options, answer_data, correctAnswerText, question });
  if (!content) return null;

  return (
    <Box>
      {usingAiFallback && (
        <Chip
          label="AI-computed answer"
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontSize: "0.65rem", height: 20, mb: 0.75 }}
        />
      )}
      {content}
    </Box>
  );
};

/** Inner renderer — returns the answer content node or null. */
const renderAnswerContent = ({ type, options, answer_data, correctAnswerText, question }) => {

  // MCQ — no options list but letter/text in answer_data (similar paper)
  if (["multiple-choice", "multiple-select", "true-false"].includes(type) && options.length === 0) {
    const ft = formatAnswerForDisplay(correctAnswerText);
    if (!ft) return null;
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Correct answer:</Typography>
        <Box sx={{ color: "success.main", fontSize: "0.875rem" }}>
          <HtmlWithLatex html={ft} />
        </Box>
      </Box>
    );
  }

  // MCQ / multiple-select / true-false
  if (["multiple-choice", "multiple-select", "true-false"].includes(type) && options.length > 0) {
    const anyCorrect = options.some((o) => o?.is_correct);
    const keyFallback = formatAnswerForDisplay(correctAnswerText);
    const hasImageOptions = options.some((o) => o.image_url);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Options
        </Typography>
        {/* Image-option display: 2-column grid of image cards */}
        {hasImageOptions ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 1.5 }}>
            {options.map((opt, idx) => {
              const label = opt.text || opt.option_text || "";
              return (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: "center",
                    borderColor: opt.is_correct ? "success.main" : "#e0e0e0",
                    borderWidth: opt.is_correct ? 2 : 1,
                    bgcolor: opt.is_correct ? "#f0fdf4" : "white",
                  }}
                >
                  {opt.image_url && (
                    <Box sx={{ mb: 0.5 }}>
                      <img
                        src={opt.image_url}
                        alt={label}
                        style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </Box>
                  )}
                  <Chip
                    label={opt.is_correct ? "Correct" : "Incorrect"}
                    size="small"
                    color={opt.is_correct ? "success" : "default"}
                    variant={opt.is_correct ? "filled" : "outlined"}
                    sx={{ fontSize: "0.65rem", mb: 0.5 }}
                  />
                  {label && (
                    <Box sx={{ display: "block", color: "text.secondary" }}>
                      <HtmlWithLatex html={label} />
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        ) : (
          /* Text-option display: existing list */
          options.map((opt, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={opt.is_correct ? "Correct" : "Incorrect"}
                  size="small"
                  color={opt.is_correct ? "success" : "default"}
                  variant={opt.is_correct ? "filled" : "outlined"}
                  sx={{ minWidth: 75, fontSize: "0.7rem" }}
                />
              </Box>
              <Box sx={{ mt: 0.5 }}>
                <HtmlWithLatex html={opt.text || opt.option_text || ""} />
              </Box>
            </Paper>
          ))
        )}
        {!anyCorrect && keyFallback && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: "#fffbeb", borderColor: "#fcd34d" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Answer key</Typography>
            <Box sx={{ color: "success.main", fontSize: "0.875rem" }}>
              <HtmlWithLatex html={keyFallback} />
            </Box>
          </Paper>
        )}
      </Box>
    );
  }

  // Essay — inline display matching examplify style
  if (type === "essay") {
    if (!correctAnswerText) return null;
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Correct Answer:</Typography>
        <Box sx={{ color: "success.main", fontSize: "0.875rem" }}>
          <HtmlWithLatex html={formatAnswerForDisplay(correctAnswerText)} />
        </Box>
      </Box>
    );
  }

  // Short answer — inline display
  if (type === "short-answer") {
    if (!correctAnswerText) return null;
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Correct Answer:</Typography>
        <Box sx={{ color: "success.main", fontSize: "0.875rem" }}>
          <HtmlWithLatex html={formatAnswerForDisplay(correctAnswerText)} />
        </Box>
      </Box>
    );
  }

  // Fill-in-the-blank / multi-fill / table / image-fill
  if (["fill-in-the-blank", "multi-fill", "table", "image-fill"].includes(type)) {
    // Primary: admin format — answer_data.correct_blanks = [["ans1", "alt"], ...]
    const blanksFromData = answer_data?.correct_blanks;
    // Optional expression labels per blank (e.g. ["½ + □ = 6", "3.5 + □ = 6"])
    const blankLabels = answer_data?.blank_labels || [];
    // Fallback: examplify format — question.blanks = [{text, order}, ...]
    const blanksFromLegacy = question.blanks;

    if (blanksFromData?.length > 0) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {type === "image-fill" ? "Answer boxes" : "Blanks"}
          </Typography>
          {blanksFromData.map((blank, idx) => {
            const label = blankLabels[idx] || null;
            const answer = Array.isArray(blank) ? blank[0] : blank;
            const altAnswers = Array.isArray(blank) && blank.length > 1 ? blank.slice(1) : [];
            const hasAnswer = String(answer ?? "").trim() || altAnswers.some((a) => String(a ?? "").trim());
            if (type === "image-fill" && !hasAnswer) return null;
            return (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {label ? (
                    <Box sx={{ flex: "1 1 120px" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        <HtmlWithLatex html={toLatexLabel(label)} />
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", flexShrink: 0 }}>
                      {type === "image-fill" ? `Box ${idx + 1}` : `Blank ${idx + 1}`}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Typography variant="body2"><strong>Answer:</strong></Typography>
                    <LatexValue value={answer} sx={{ color: "success.main", fontWeight: 600 }} />
                    {altAnswers.length > 0 && (
                      <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", ml: 0.5 }}>
                        (also:{" "}
                        {altAnswers.map((a, ai) => (
                          <span key={ai}>
                            {ai > 0 && ", "}
                            <LatexValue value={a} />
                          </span>
                        ))}
                        )
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      );
    }

    if (blanksFromLegacy?.length > 0) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Blanks
          </Typography>
          {blanksFromLegacy.map((blank, idx) => {
            const label = blankLabels[idx] || null;
            return (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {label ? (
                    <Box sx={{ flex: "1 1 120px" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        <HtmlWithLatex html={toLatexLabel(label)} />
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", flexShrink: 0 }}>
                      Blank {(blank.order ?? idx) + 1}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Typography variant="body2"><strong>Answer:</strong></Typography>
                    <LatexValue value={blank.text || ""} sx={{ color: "success.main", fontWeight: 600 }} />
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      );
    }

    const fallbackFill = formatAnswerForDisplay(
      answer_data?.correct_answer_text ?? answer_data?.model_answer ?? ""
    );
    if (fallbackFill) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Correct answer:</Typography>
          <Box sx={{ color: "success.main", fontSize: "0.875rem" }}>
            <HtmlWithLatex html={fallbackFill} />
          </Box>
        </Box>
      );
    }

    return null;
  }

  // Ordering
  if (type === "ordering" && options.length > 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Ordering Items
        </Typography>
        {options.map((opt, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
            {opt.image_url && (
              <Box sx={{ mb: 1 }}>
                <img
                  src={opt.image_url}
                  alt={opt.text || opt.option_text || `Item ${idx + 1}`}
                  style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Box>
            )}
            <Typography variant="body2">
              <strong>Order: {idx + 1}</strong>
            </Typography>
            <Box>
              <Typography component="span" variant="body2"><strong>Ordering Item Text: </strong></Typography>
              <HtmlWithLatex html={opt.text || opt.option_text || ""} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Matching
  if (type === "matching" && options.length > 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Matching Pairs
        </Typography>
        {options.map((opt, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
            {opt.image_url && (
              <Box sx={{ mb: 1 }}>
                <img
                  src={opt.image_url}
                  alt={opt.text || opt.option_text || `Pair ${idx + 1}`}
                  style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
              <Box sx={{ fontWeight: 700 }}>
                <HtmlWithLatex html={opt.text || opt.option_text || ""} />
              </Box>
              <Typography component="span" variant="body2">→</Typography>
              <LatexValue value={opt.match_text || ""} sx={{ color: "success.main" }} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return null;
};

export default QuestionAnswerDisplay;
